<template>
  <b-modal
      id="memberUploadMediaModal"
      modal-class="client-details-modal"
      centered
      size="lg"
      hide-footer
      no-close-on-backdrop
      hide-header-close
      @shown="fetchFiles"
  >
    <!-- Modal Header -->
    <template #modal-header>
      <div class="view-clients-header">
        <b-button variant="close-btn" class="btn-container" @click="closeModal">
          <i class="fas fa-times"></i>
        </b-button>
      </div>
    </template>

    <b-container fluid ref="memberDocContainer">
      <!-- Upload Section -->
      <div class="form-part">
        <div class="title-text text-center mb-3">
          <h5><strong>Upload Media & Files</strong></h5>
        </div>
        <div class="mt-4">
          <div class="mb-4">
            <label for="file-name">File Title <span class="text-danger">*</span></label>
            <input
                type="text"
                id="file-name"
                v-model="title"
                class="memberSearch-field col-6 mt-2"
                placeholder="Title"
                @input="resetTitleError"
            />
            <div v-if="titleError" class="form-error error-font-size mt-2">
              {{ titleError }}
            </div>
          </div>
          <div class="file-upload-container" @click="triggerFileInput">
            <i class="fas fa-file-excel"></i>
            <span>Click to select files and media.</span>
            <input
                type="file"
                ref="fileInput"
                @change="handleFileChange"
                class="d-none"
            />
          </div>
          <div v-if="errorExcelError" class="form-error error-font-size mt-2 pl-2">
            {{ errorExcelError }}
          </div>
          <div v-if="fileName" class="mt-4">
            <div class="mb-2 excel-file-title">Selected File:</div>
            <div class="file-list-item">
              <span class="file-item-name">{{ fileName }}</span>
              <span class="file-item-actions">
                <button
                    type="button"
                    title="View"
                    class="file-action-btn file-action-view"
                    @click="viewSelectedFile"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button
                    type="button"
                    title="Download"
                    class="file-action-btn file-action-download"
                    @click="downloadSelectedFile"
                >
                  <i class="fas fa-download"></i>
                </button>
                <button
                    type="button"
                    title="Remove"
                    class="file-action-btn file-action-delete"
                    @click="confirmDeleteFile"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </span>
            </div>
          </div>
          <div class="d-flex justify-content-end mt-4">
            <button type="submit" @click="uploadFile" class="group-univ-btn text-center mt-0">
              Submit
            </button>
          </div>
        </div>
      </div>

      <!-- File List Section -->
      <div v-if="files.length > 0" class="view-clients-modal-content">
        <h6>File List</h6>
        <ul class="client-docs">
          <template v-for="(fileItem, fileItemIndex) in files">
            <li
                v-for="(file, subIndex) in fileItem.files"
                :key="subIndex"
                class="file-list-item"
            >
              <b-form-checkbox
                  :id="`checkbox-${fileItemIndex}-${subIndex}`"
                  :name="`checkbox-${fileItemIndex}-${subIndex}`"
                  v-model="form.data"
                  :value="file"
              />
              <span class="file-item-name">{{ fileItem.title }}</span>
              <span class="file-item-actions">
                <a
                    :href="file.filepath"
                    target="_blank"
                    title="View"
                    class="file-action-btn file-action-view"
                >
                  <i class="fas fa-eye"></i>
                </a>
                <button
                    type="button"
                    title="Download"
                    class="file-action-btn file-action-download"
                    @click="downloadFile(file.filepath, fileItem.title)"
                >
                  <i class="fas fa-download"></i>
                </button>
                <button
                    type="button"
                    title="Delete"
                    class="file-action-btn file-action-delete"
                    @click="deleteUploadedFile(file, fileItemIndex, subIndex)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </span>
            </li>
          </template>
        </ul>
        <div class="file-note mt-3">
          <i class="fas fa-info-circle"></i>
          <span>Please choose one or many files to be sent in an email.</span>
        </div>
        <button
            type="button"
            class="btn univ-btn btn-secondary"
            @click="sendCardEmail"
            :disabled="isDisable"
        >
          {{ isSend ? "Sending File" : "Send File" }}
        </button>
      </div>
    </b-container>
  </b-modal>
</template>

<script>
import Api from "@/resource/Api";

export default {
  name: "MemberUploadMediaModal",
  props: {
    member: {
      required: true,
    },
  },
  data() {
    return {
      files: [],
      file: null,
      title: "",
      fileName: "",
      errorExcelError: "",
      titleError: "",
      isDisable: true,
      isSend: false,
      form: {data: []},
    };
  },
  watch: {
    "form.data"(newVal) {
      // Enable or disable the send button
      this.isDisable = newVal.length === 0;
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    resetTitleError() {
      this.titleError = ""; // Real-time removal of titleError
    },
    handleFileChange(event) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        this.file = selectedFile;
        this.fileName = selectedFile.name;
        this.errorExcelError = ""; // Clear error if file is selected
      }
    },
    resetState() {
      this.file = null;
      this.fileName = "";
      this.errorExcelError = "";
      this.titleError = "";
      this.title = "";
      this.form.data = []
    },
    confirmDeleteFile() {
      this.$confirm({
        title: "Confirm",
        message: `Are you sure you want to delete ${this.fileName}?`,
        button: {no: "No", yes: "Yes"},
        callback: (confirm) => {
          if (confirm) this.resetState();
        },
      });
    },
    uploadFile() {
      this.titleError = !this.title ? "Title is required in order to proceed." : "";
      this.errorExcelError = !this.file ? "File is required in order to proceed." : "";

      if (this.titleError || this.errorExcelError) return;

      const formData = new FormData();
      formData.append("files[]", this.file);
      formData.append("title", this.title);
      formData.append("member_id", this.member.user_id);

      const loader = this.showLoader();
      Api.post("v2/member-file-upload/upload-files", formData, {
        headers: {"Content-Type": "multipart/form-data"},
      })
          .then((response) => {
            this.$notify(response.data.message);
            this.handleUploadSuccess();
          })
          .catch((err) => {
            this.$notify(err.response?.data?.message || "An error occurred.", "error");
          })
          .finally(() => loader.hide());
    },
    handleUploadSuccess() {
      this.resetState();
      this.fetchFiles();
    },
    fetchFiles() {
      const loader = this.showLoader();
      Api.get(`v2/member-file-upload/get-files?member_id=${this.member.user_id}`)
          .then((res) => {
            this.files = res.data.data || [];
          })
          .catch((err) => {
            this.$notify(err.response?.data?.message || "Failed to fetch files.", "error");
          })
          .finally(() => loader.hide());
    },
    sendCardEmail() {
      const requestData = {
        member_email: this.member.user_email,
        filepath: this.form.data.map((file) => file.filepath),
        member_name: `${this.member.first_name} ${this.member.last_name}`,
      };

      const loader = this.showLoader({container: this.$refs.memberDocContainer});
      this.isSend = true;

      Api.post("v2/member-file-upload/send-member-email", requestData)
          .then((res) => {
            this.$notify(res.data.message);
            this.closeModal();
          })
          .catch((err) => {
            this.$notify(err.response?.data?.message || "Failed to send email.", "error");
          })
          .finally(() => {
            this.isSend = false;
            loader.hide();
          });
    },
    viewSelectedFile() {
      if (!this.file) return;
      const url = URL.createObjectURL(this.file);
      window.open(url, "_blank");
    },
    downloadSelectedFile() {
      if (!this.file) return;
      const url = URL.createObjectURL(this.file);
      const link = document.createElement("a");
      link.href = url;
      link.download = this.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadFile(filepath, filename) {
      const link = document.createElement("a");
      link.href = filepath;
      link.download = filename || "download";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    deleteUploadedFile(file, fileItemIndex, subIndex) {
      this.$confirm({
        title: "Confirm Delete",
        message: "Are you sure you want to delete this file?",
        button: { no: "No", yes: "Yes" },
        callback: (confirm) => {
          if (!confirm) return;
          const loader = this.showLoader();
          Api.delete(`v2/member-file-upload/delete-file/${file.id}`)
              .then((res) => {
                this.$notify(res.data.message || "File deleted successfully.");
                this.files[fileItemIndex].files.splice(subIndex, 1);
                if (this.files[fileItemIndex].files.length === 0) {
                  this.files.splice(fileItemIndex, 1);
                }
                this.form.data = this.form.data.filter((f) => f !== file);
              })
              .catch((err) => {
                this.$notify(err.response?.data?.message || "Failed to delete file.", "error");
              })
              .finally(() => loader.hide());
        },
      });
    },
    closeModal() {
      this.resetState();
      this.files = [];
      this.$bvModal.hide("memberUploadMediaModal");
      this.$emit("onCloseModal");
    },
    showLoader(options = {}) {
      return this.$loading.show(options);
    },
  },
};
</script>

<style scoped>
.view-clients-modal-content h6 {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #7a8fa6;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f0f7;
}

.client-docs {
  list-style: none;
  margin: 0;
  padding: 0;
}

.file-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f8fbfd;
  border: 1px solid #e4edf5;
  margin-bottom: 8px;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.file-list-item:hover {
  border-color: #b6d8ef;
  box-shadow: 0 2px 8px rgba(5, 103, 188, 0.08);
}

.file-item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 500;
  color: #2d4a60;
}

.file-item-name::before {
  content: "\f15b";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  margin-right: 7px;
  color: #a0b9cc;
  font-size: 12px;
}

.file-item-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.file-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  text-decoration: none;
  transition: background 0.15s, transform 0.1s;
}

.file-action-btn:active {
  transform: scale(0.92);
}

.file-action-view {
  background: #e6f4fb;
  color: #1597b8;
}
.file-action-view:hover {
  background: #1597b8;
  color: #fff;
}

.file-action-download {
  background: #e8f7ee;
  color: #28a745;
}
.file-action-download:hover {
  background: #28a745;
  color: #fff;
}

.file-action-delete {
  background: #fdeeed;
  color: #ea584e;
}
.file-action-delete:hover {
  background: #ea584e;
  color: #fff;
}

.file-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #f0f7ff;
  border-left: 3px solid #1597b8;
  border-radius: 0 6px 6px 0;
  padding: 8px 12px;
  margin-top: 4px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #4a6375;
}

.file-note i {
  margin-top: 2px;
  color: #1597b8;
  flex-shrink: 0;
}
</style>